import { useMemo } from "react";

import shuffle from "lodash/shuffle";

import { experiments, getExperimentWithExposures } from "@/utils/experiment";

import {
    localStorageData,
    queryParam,
    useRegisterMetadata
} from "../hooks/use-registered-metadata";
import { useJfindSimplifiedListViewWithBasicTasksUpdateTargetingGateEnabled } from "../hooks/use-simplified-list-view-targeting-gate-enabled";
import {
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    UserSegmentationTeamType,
    wacToIdentityUserSegMap
} from "../models/user-segmentation";
import {
    getTeamTypeToCheckboxMap,
    checkboxValues
} from "../views/jtbd/constants";

export const getDefaultValueFromTeamType = (
    teamType: UserSegmentationTeamType,
    isGateEnabled: boolean
) => {
    const map = getTeamTypeToCheckboxMap(isGateEnabled);
    return map[teamType]?.defaultValues;
};

export const mapJTBDtoTemplateChoice = (
    checkboxes: checkboxValues[],
    teamType: string | undefined,
    isGateEnabled: boolean
) => {
    const checkedBoxes = checkboxes.filter((checkbox) => checkbox.isChecked);

    const teamTypeDefaultValues = teamType
        ? getDefaultValueFromTeamType(
              teamType as UserSegmentationTeamType,
              isGateEnabled
          )
        : undefined;

    if (checkedBoxes.length === 0 && teamTypeDefaultValues) {
        return teamTypeDefaultValues;
    }
    const sortedCheckboxes = checkedBoxes.sort(
        (a, b) => a.priority - b.priority
    );

    return {
        template: sortedCheckboxes[0]?.template,
        persona: sortedCheckboxes[0]?.persona
    };
};
/**
 * Returns current URL with query parameters added
 *
 * @param params {object} - query parameters to add
 *
 * @returns {string} - URL with query parameters
 */
export function getCurrentUrlWithParams(params: {
    [key: string]: string | string[];
}) {
    const url = new URL(window.location.href);
    Object.entries(params).forEach(([param, value]) => {
        if (Array.isArray(value)) {
            const existingValues =
                url.searchParams.get(param)?.split(",") || [];
            const newValues = [...existingValues, ...value];
            url.searchParams.set(param, newValues.join(","));
        } else {
            url.searchParams.set(param, value);
        }
    });
    return url.toString();
}

export const getJtbdTemplateOverrideQueryParam = () => {
    return (
        new URLSearchParams(window.location.search).get(
            "jtbdTemplateOverride"
        ) ?? undefined
    );
};

export const TemplateQueryParamScrum = "scrum";

export const ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN: UserSegmentationTeamType[] = [
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS
];

const hasValidCgsKeyInUrl = () => {
    const isWindowExist = typeof window !== "undefined";

    if (!isWindowExist) {
        return false;
    }

    const url = new URL(window.location.href);
    const cgsKeyValue = url.searchParams.get("cgsKey");

    return cgsKeyValue && cgsKeyValue.trim() !== "";
};

const isEnrolledInTreatment = (
    experiment: (typeof experiments)[keyof typeof experiments]
) => {
    const experimentState = getExperimentWithExposures(experiment);

    return (
        experimentState.type === "enrolled" && experimentState.value === true
    );
};

export const useEligibleTeamTypeForJtbdLocalStorage = () => {
    const metadata = useRegisterMetadata();
    const foundTeamType = ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.find(
        (teamType) =>
            metadata?.get(localStorageData.team) ===
            wacToIdentityUserSegMap[teamType]
    );

    return foundTeamType;
};

export const useGetTeamTypeData = () => {
    const metadata = useRegisterMetadata();
    const isGateEnabled =
        useJfindSimplifiedListViewWithBasicTasksUpdateTargetingGateEnabled();
    const teamType = useMemo(() => {
        // Reconcile team between query params and local storage
        const foundTeamType = ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.find(
            (teamType) =>
                metadata?.get(queryParam.teamType) === teamType ||
                metadata?.get(localStorageData.team) ===
                    wacToIdentityUserSegMap[teamType]
        );

        return foundTeamType ?? undefined;
    }, [metadata]);

    const isSoftwareTeamType =
        teamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;

    // Get the map at runtime to ensure feature flag is checked when needed
    const teamTypeToCheckboxMap = useMemo(
        () => getTeamTypeToCheckboxMap(isGateEnabled),
        [isGateEnabled]
    );

    if (isSoftwareTeamType) {
        const newTeamTypeCheckboxes = shuffle(
            teamTypeToCheckboxMap[teamType]?.checkboxValues
        );
        return {
            teamType,
            teamTypeCheckboxes: newTeamTypeCheckboxes,
            isSoftwareTeamType
        };
    }

    const teamTypeCheckboxes = teamType
        ? teamTypeToCheckboxMap[teamType]?.checkboxValues
        : undefined;

    return { teamType, teamTypeCheckboxes };
};

export const isEligibleTeamTypeToViewJtbdScreen = (
    teamType: UserSegmentationTeamType
) => {
    const teamTypesSkippingJtbd = [
        USER_SEGMENTATION_TEAM_TYPE_MARKETING,
        USER_SEGMENTATION_TEAM_TYPE_OPERATIONS
    ];

    if (!ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.includes(teamType)) {
        return false;
    }

    // Skip the JTBD screen for marketing and operations team types.
    // Jira FFL takes priority for CGS-keyed entrypoints, so those users do not
    // become candidates for MadLib exposures.
    if (teamTypesSkippingJtbd.includes(teamType)) {
        if (hasValidCgsKeyInUrl()) {
            const inJiraFflTreatment = isEnrolledInTreatment(
                experiments.cfind4666JiraFfl
            );

            return !inJiraFflTreatment;
        }

        const inMadLibTreatment = isEnrolledInTreatment(
            experiments.jiraOnboardingAiMadlibV2
        );

        return !inMadLibTreatment;
    }

    return true;
};
